import React, { useEffect, useState, useMemo, useRef } from "react";
import { Layout } from "antd";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import SidebarMenu from "../components/SidebarMenu/SidebarMenu";
import { Module } from "../types/Module";
import { customThemes } from "../themeConfig";

const { Sider } = Layout;

type SidebarProps = {
  collapsed: boolean;
  darkMode: boolean;
  isMobile: boolean;

  // ✅ ADD THIS
  themeStyles: {
    siderBg: string;
    headerBg: string;
    layoutBg: string;
    contentBg: string;
    contentText: string;
    footerBg: string;
    colorPrimary: string;
  };
};

const Sidebar: React.FC<SidebarProps> = ({ collapsed, darkMode, isMobile }) => {
  const location = useLocation();

  const [selectedKey, setSelectedKey] = useState<string>("1");
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  // Redux modules
  const rawModules = useSelector((state: any) => state.user?.modules as Module[]);

  const modules = useMemo<Module[]>(() => {
    return rawModules?.filter((m: any) => m?.module_type === "page") || [];
  }, [rawModules]);

  const user = useSelector((state: any) => state.user?.user);

  const logo_url =
    user?.organization_logo && process.env.REACT_APP_FILE_URL
      ? `${process.env.REACT_APP_FILE_URL}uploads/organizations/logo/${user.organization_logo}`
      : "";

  const currentPath = location.pathname;
  const themeStyles = darkMode ? customThemes.dark : customThemes.light;

  const siderRef = useRef<HTMLDivElement>(null);

  // ✅ Selected menu
  useEffect(() => {
    let activeKey = "1";

    for (const module of modules) {
      if (module.module_url === currentPath) {
        activeKey = String(module.module_id);
        break;
      }

      if (module.children?.some((c) => c.module_url === currentPath)) {
        activeKey = String(module.module_id);
        break;
      }
    }

    setSelectedKey(activeKey);
  }, [currentPath, modules]);

  // ✅ Open submenu
  useEffect(() => {
    const openSubKeys = modules
      .filter((m) => m.children?.some((c) => c.module_url === currentPath))
      .map((m) => String(m.module_id));

    setOpenKeys(openSubKeys);
  }, [currentPath, modules]);

  return (
    <Sider
      ref={siderRef}
      trigger={null}
      width={296}
      collapsible
      collapsed={collapsed}
      breakpoint="lg"
      collapsedWidth={isMobile ? 0 : 70}
      theme={darkMode ? "light" : "dark"}
      style={{
        overflow: "auto",
        height: "100vh",
        position: "sticky",
        insetInlineStart: 0,
        top: 0,
        bottom: 0,
        scrollbarWidth: "thin",
        scrollbarGutter: "stable",
        backgroundColor: themeStyles.siderBg,
      }}
    >
      {/* Logo */}
      <div
        style={{
          height: 64,
          margin: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={
            collapsed
              ? logo_url || "/favicon2.ico"
              : logo_url || "/logo.png"
          }
          alt="Logo"
          style={{
            height: 32,
            transition: "all 0.3s ease",
            objectFit: "contain",
          }}
        />
      </div>

      {/* Sidebar Menu */}
      <SidebarMenu
        modules={modules}
        darkMode={darkMode}
        siderRef={siderRef}
        collapsed={collapsed}
        selectedKey={selectedKey}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
      />
    </Sider>
  );
};

export default Sidebar;